import { useSelector } from 'react-redux';
import { type PortfolioAmount, PortfolioProductPricing } from '@mews-commander/data-access';
import { LocalizedCurrency, LocalizedPercentage, lt, useFeatureFlag } from '@mews/framework';
import { DividerList, LabeledListItem, OptimusProvider, TooltippedIconButton } from '@mews-commander/ui';
import { UnicodeEntity } from '@mews/enums';
import { ApplicationState, TaxRateStrategyDiscriminator } from '@mews-commander/types';
import { getTaxationByTaxRateCode } from '@mews-commander/legacy-entities/src/ducks/taxEnvironments';
import { FeatureFlag } from '@mews-commander/enums';
import { IconName, Position, Stack, Typography } from '@optimus-web/core';
import { useDialogForm } from '@mews-commander/form';
import { EditProductPricingForm } from './forms/EditProductPricingForm/Form';
import type { LocalProductSidePanelProps } from './types';

export const LocalProductPricing = (props: LocalProductSidePanelProps<'pricing'>) => {
    const { value, discriminator, propertyId, enterpriseProductId, centralProductId } = props;
    const centralProductsEditEnabled = useFeatureFlag(FeatureFlag.centralProductsEditEnabled);

    const taxRateCodes =
        discriminator === PortfolioProductPricing.Absolute
            ? value.taxValues.map(({ taxRateCode }) => taxRateCode).filter((code) => code != null)
            : value.taxRateCodes;

    const taxationMapping = useSelector((state: ApplicationState) => getTaxationByTaxRateCode(state, { taxRateCodes }));

    const [renderEditProductPricingForm, toggleEditProductPricingForm] = useDialogForm({
        title: lt('EnterpriseProductEditPricingDialogTitle'),
        cancelLabel: lt('GoBack'),
        renderForm: (formProps) => (
            <EditProductPricingForm
                {...formProps}
                value={value as PortfolioAmount}
                centralProductId={centralProductId as string}
                enterpriseProductId={enterpriseProductId}
                propertyId={propertyId}
                formRef={formProps.formRef}
                taxRateCodes={taxRateCodes}
            />
        ),
    });

    return (
        <>
            {centralProductsEditEnabled && (
                <Stack distribution="space-between" alignment="center">
                    <Typography as="div" textStyle="titleSmallStrong">
                        {lt('PricingDetails')}
                    </Typography>
                    <TooltippedIconButton
                        tooltipPosition={Position.Left}
                        tooltip={lt('EditPricingProductTooltip')}
                        type="button"
                        size="small"
                        icon={IconName.Edit}
                        onClick={toggleEditProductPricingForm}
                    />
                </Stack>
            )}
            <DividerList>
                <LabeledListItem data-test-id="basePrice" label={lt('BasePrice')}>
                    {discriminator === PortfolioProductPricing.Absolute ? (
                        <LocalizedCurrency currency={value.currency} value={value.netValue} />
                    ) : (
                        <LocalizedPercentage value={value.multiplier} />
                    )}
                </LabeledListItem>
                {taxRateCodes.length > 0 ? (
                    <>
                        {Object.values(taxationMapping).map(({ name, localName, taxStrategy }) => (
                            <LabeledListItem key={name} data-test-id={name} label={localName || name}>
                                {taxStrategy?.discriminator === TaxRateStrategyDiscriminator.Flat ? (
                                    <LocalizedCurrency
                                        value={taxStrategy?.value.value}
                                        currency={taxStrategy?.value.currencyCode}
                                    />
                                ) : (
                                    <LocalizedPercentage
                                        value={taxStrategy?.value.value || 0}
                                        options={{ maximumFractionDigits: 3 }}
                                    />
                                )}
                            </LabeledListItem>
                        ))}
                    </>
                ) : (
                    <LabeledListItem data-test-id="taxRate" label={lt('TaxRate')}>
                        {UnicodeEntity.Dash}
                    </LabeledListItem>
                )}
            </DividerList>
            {discriminator === PortfolioProductPricing.Absolute && centralProductId ? (
                <OptimusProvider density="spacious">{renderEditProductPricingForm()}</OptimusProvider>
            ) : null}
        </>
    );
};
